/**
 * 品类大盘模块
 * @description 展示3个KPI卡片，支持数据动态更新
 */
import React, { useReducer, useMemo, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Row, Col } from 'tea-component';
import KpiCard from '../KpiCard';
import useCancelRequest, { IRequestToken } from '@intelligence/hooks/useCancelRequest';
import { fetchCategoryOverviewKpi, fetchCategoryOverviewChart } from '../../services';
import { formatKMBValue, filterRevenueMetrics } from '../../utils';
import { CATEGORY_KPI_TIPS_I18N, IKpiCardConfigItem, getDataSourceMetricKey, getKpiCardsConfig } from '../../constants';

import type { ICasualCompetitorsDataSourceProps, ICasualCompetitorsParams } from '../../types';

import './index.scss';

/**
 * 模块实例接口
 */
export interface ICategoryOverviewInstance {
  update: (params: ICasualCompetitorsParams) => void;
}

interface ICategoryOverviewProps extends IBaseProps, ICasualCompetitorsDataSourceProps {
  categoryKpiTipsI18n?: IRecord<string>;
  params: ICasualCompetitorsParams | null;
}

interface IKpiItemData {
  value: number;
  ratio: number;
  prevValue: number;
}

interface IChartDataItem {
  date: string;
  value: number;
  name: string;
  ratio?: number;
  prevValue?: number;
}

interface ICategoryState {
  kpiLoading: boolean;
  chartLoading: boolean;
  kpiData: Record<string, IKpiItemData>;
  chartData: Record<string, IChartDataItem[]>;
}

type TCategoryAction =
  | { type: 'SET_KPI_LOADING'; payload: boolean }
  | { type: 'SET_CHART_LOADING'; payload: boolean }
  | { type: 'SET_KPI_DATA'; payload: Record<string, IKpiItemData> }
  | { type: 'SET_CHART_DATA'; payload: Record<string, IChartDataItem[]> }
  | { type: 'RESET' };

const initialState: ICategoryState = {
  kpiLoading: true,
  chartLoading: true,
  kpiData: {},
  chartData: {},
};

const reducer = (state: ICategoryState, action: TCategoryAction): ICategoryState => {
  switch (action.type) {
    case 'SET_KPI_LOADING':
      return { ...state, kpiLoading: action.payload };
    case 'SET_CHART_LOADING':
      return { ...state, chartLoading: action.payload };
    case 'SET_KPI_DATA':
      return { ...state, kpiLoading: false, kpiData: action.payload };
    case 'SET_CHART_DATA':
      return { ...state, chartLoading: false, chartData: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

const CategoryOverview: React.ForwardRefRenderFunction<ICategoryOverviewInstance, ICategoryOverviewProps> = (
  props,
  ref,
) => {
  const { style, className, categoryKpiTipsI18n = CATEGORY_KPI_TIPS_I18N, dataSource, params } = props;
  const { formatMessage } = useIntl();

  const [state, dispatch] = useReducer(reducer, initialState);
  const { kpiLoading, chartLoading, kpiData, chartData } = state;

  const { next: nextRequest } = useCancelRequest();

  const kpiCardsConfig = useMemo(() => getKpiCardsConfig(dataSource), [dataSource]);
  const iapMetricKey = getDataSourceMetricKey(dataSource, 'iap_revenue');

  const getMetricsList = (
    granularity: 'monthly' | 'daily' | (string & {}),
    showRevenue: boolean,
    excludeMetrics: string[] = [],
  ): string[] => {
    const config = kpiCardsConfig[granularity];
    let metrics = config.map((item) => item.metricKey);

    // 使用统一方法过滤收入类指标
    metrics = filterRevenueMetrics(metrics, showRevenue);

    if (excludeMetrics.length > 0) {
      // 排除指定指标（严格相等匹配）
      metrics = metrics.filter((key) => !excludeMetrics.includes(key));
    }

    return metrics;
  };

  /**
   * 获取KPI数据
   */
  const fetchKpiData = async (params: ICasualCompetitorsParams, { cancelToken, isStale }: IRequestToken) => {
    const { showRevenue, granularity, startDate, endDate, markets, platforms, genreId, isIAAArrows, revenueScope } =
      params;
    const metrics = getMetricsList(granularity, showRevenue);

    dispatch({ type: 'SET_KPI_LOADING', payload: true });

    try {
      const res = await fetchCategoryOverviewKpi(
        {
          start_date: startDate,
          end_date: endDate,
          granularity,
          metrics,
          markets,
          platforms,
          revenue_scope: revenueScope,
          filter: {
            genre_id: genreId,
            enable_game_id_filter: isIAAArrows,
          },
        },
        { cancelToken },
      );

      if (isStale()) {
        return;
      }

      const list = (res as any)?.data?.list || [];
      if (list.length > 0) {
        const item = list[0];
        const newKpiData: Record<string, IKpiItemData> = {};

        metrics.forEach((metricKey) => {
          newKpiData[metricKey] = {
            value: item[metricKey] ?? 0,
            ratio: item[`${metricKey}_ratio`] ?? 0,
            prevValue: item[`${metricKey}_prev`] ?? 0,
          };
        });

        dispatch({ type: 'SET_KPI_DATA', payload: newKpiData });
      } else {
        dispatch({ type: 'SET_KPI_DATA', payload: {} });
      }
    } catch (error) {
      console.error('fetchCategoryOverviewKpi error:', error);
      if (isStale()) {
        return;
      }
      dispatch({ type: 'SET_KPI_DATA', payload: {} });
    }
  };

  /**
   * 获取折线图数据 - 单次请求获取所有指标
   */
  const fetchChartData = async (params: ICasualCompetitorsParams, { cancelToken, isStale }: IRequestToken) => {
    const { showRevenue, granularity, startDate, endDate, markets, platforms, genreId, isIAAArrows, revenueScope } =
      params;
    const metrics = getMetricsList(granularity, showRevenue);

    dispatch({ type: 'SET_CHART_LOADING', payload: true });

    try {
      const res = await fetchCategoryOverviewChart(
        {
          start_date: startDate,
          end_date: endDate,
          granularity,
          metrics,
          markets,
          platforms,
          revenue_scope: revenueScope,
          filter: {
            genre_id: genreId,
            enable_game_id_filter: isIAAArrows,
          },
        },
        { cancelToken },
      );

      if (isStale()) {
        return;
      }

      const list = (res as any)?.data?.list || [];

      // 按指标分类处理返回数据
      const newChartData: Record<string, IChartDataItem[]> = {};
      metrics.forEach((metricKey) => {
        const chartItems = list.map((item: any) => ({
          date: item.date || '',
          value: item[metricKey] ?? 0,
          name: metricKey,
          ratio: item[`${metricKey}_ratio`] ?? 0,
          prevValue: item[`${metricKey}_prev`] ?? 0,
        }));
        // 按日期升序排序，确保最后一项为最新数据
        chartItems.sort((a: any, b: any) => (a?.date || '').localeCompare(b?.date || ''));
        newChartData[metricKey] = chartItems;
      });

      dispatch({ type: 'SET_CHART_DATA', payload: newChartData });
    } catch (error) {
      console.error('fetchCategoryOverviewChart error:', error);
      if (isStale()) {
        return;
      }
      dispatch({ type: 'SET_CHART_DATA', payload: {} });
    }
  };

  /**
   * 暴露update方法给外层
   */
  useImperativeHandle(ref, () => ({
    update: (params: ICasualCompetitorsParams) => {
      const requestToken = nextRequest();

      // KPI 请求
      if (params.isIAAAll || !params.isSingleDate) {
        fetchKpiData(params, requestToken);
      } else {
        // 清空 KPI 数据，使用折线图数据
        dispatch({ type: 'SET_KPI_DATA', payload: {} });
        dispatch({ type: 'SET_KPI_LOADING', payload: false });
      }

      // 折线图请求（使用 chartStartDate）
      fetchChartData({ ...params, startDate: params.chartStartDate }, requestToken);
    },
  }));

  /**
   * 当前KPI配置
   */
  const currentKpiConfig = useMemo<IKpiCardConfigItem[]>(() => {
    if (!params) return [];
    const { granularity, showRevenue } = params;
    const config = kpiCardsConfig[granularity];

    if (!showRevenue) {
      return config.filter((item) => item.metricKey !== iapMetricKey);
    }
    return config;
  }, [iapMetricKey, kpiCardsConfig, params]);

  /**
   * 渲染KPI卡片
   */
  const renderKpiCards = () => {
    // 根据卡片数量动态计算 span 值：2个卡片时 span=12，3个卡片时 span=8
    const cardCount = currentKpiConfig.length;
    const colSpan = cardCount > 0 ? Math.floor(24 / cardCount) : 8;
    const granularity = params?.granularity || 'monthly';
    const isIAAAll = params?.isIAAAll ?? false;
    const isSingleDate = params?.isSingleDate ?? false;

    return currentKpiConfig.map((card, index) => {
      const kpiApiData = kpiData[card.metricKey];
      const cardChartData = chartData[card.metricKey] || [];

      // 根据 isSingleDate 决定数据来源
      let value: number | undefined;
      let ratio: number | undefined;
      let prevValue: number | undefined;

      if (!isIAAAll && isSingleDate && cardChartData.length > 0) {
        // 单日/单月：从折线图最后一条数据取值（已按日期升序排序）
        const lastItem = cardChartData[cardChartData.length - 1];
        value = lastItem?.value;
        ratio = lastItem?.ratio;
        prevValue = lastItem?.prevValue;
      } else {
        // 非单日/单月：从 KPI 接口数据取值
        value = kpiApiData?.value;
        ratio = kpiApiData?.ratio;
        prevValue = kpiApiData?.prevValue;
      }

      return (
        <Col key={card.metricKey || index} span={colSpan}>
          <KpiCard
            loading={kpiLoading || chartLoading}
            title={formatMessage({ id: card.title })}
            isFirstCard={index === 0}
            tipsI18nKey={categoryKpiTipsI18n?.[card.tipsKey]}
            metricKey={card.metricKey}
            granularity={granularity}
            startDate={params?.startDate}
            endDate={params?.endDate}
            prevValue={prevValue}
            value={value}
            ratio={ratio}
            chartData={cardChartData}
            chartLoading={chartLoading}
            formatRule="bmk00"
            formatValue={(val, key) => {
              const formatted = formatKMBValue(val);
              // 收入类指标添加美元符号
              return key?.includes('iap_revenue') ? `$${formatted}` : formatted;
            }}
          />
        </Col>
      );
    });
  };

  return (
    <div style={style} className={classnames('dbh-panel-category-overview', className)}>
      <Row gap={20}>{renderKpiCards()}</Row>
    </div>
  );
};

// @ts-ignore
export default React.forwardRef(CategoryOverview);
